/**
 * Shared Styles for Modern Results Components
 *
 * Design System: Y2K + Glassmorphism
 * - Gradient score cards with category-based colors
 * - White glassmorphism content cards (WCAG AAA: 15.8:1 contrast)
 * - Consistent spacing, shadows, and border radii
 *
 * Used by: ModernResultsPreview, ModernResultsFull
 */

import { StyleSheet, ViewStyle } from "react-native";
import { createBoxShadow } from "../../lib/design-tokens";

// Re-export category styles from single source of truth
export { CATEGORY_STYLES, getCategoryFromScore, getCategoryStyle } from '../../lib/categoryStyles';

/**
 * Voice style to color mapping for tier-aware narrator text.
 * Used by TierBody color prop in narrator/summary cards.
 *
 * - playful: Purple/primary tone (L1 tier - free/anonymous)
 * - balanced: Gray-700 (L2 tier - drama_plus)
 * - professional: Gray-800 (L3 tier - dramax)
 */
export const voiceStyleColors: Record<'playful' | 'balanced' | 'professional', string> = {
  playful: 'text-primary-600',
  balanced: 'text-gray-700',
  professional: 'text-gray-800',
};

/**
 * Platform-aware shadow styles for web/native compatibility
 * MUST be defined before StyleSheet.create() to avoid temporal dead zone error
 */
const scoreCardShadow = createBoxShadow({
  color: "#000",
  offsetX: 0,
  offsetY: 8,
  blurRadius: 16,
  opacity: 0.3,
  elevation: 12,
});

const glassCardShadow = createBoxShadow({
  color: "#000",
  offsetX: 0,
  offsetY: 4,
  blurRadius: 12,
  opacity: 0.1,
  elevation: 6,
});

const featureCardShadow = createBoxShadow({
  color: "#000",
  offsetX: 0,
  offsetY: 2,
  blurRadius: 8,
  opacity: 0.05,
  elevation: 3,
});

/**
 * Shared StyleSheet for both Preview and Full components
 */
export const sharedStyles = StyleSheet.create({
  // Container
  container: {
    flex: 1,
  },

  // Score Card - Gradient background
  scoreCardGradient: {
    padding: 40,
    alignItems: "center",
    alignSelf: "center",
    marginBottom: 16,
    borderRadius: 16,
    minWidth: 200,
    maxWidth: 400,
    width: "100%",
    ...scoreCardShadow,
  } as ViewStyle,

  // Score number container
  scoreNumber: {
    marginBottom: 8,
  },

  // Category badge on gradient background (semi-transparent overlay)
  categoryBadgeOnGradient: {
    marginTop: 16,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "rgba(255, 255, 255, 0.2)",
  },

  // Category icon styling
  categoryIcon: {
    fontWeight: "600",
  },

  // White glassmorphism content card (WCAG AAA compliant: 15.8:1 contrast)
  glassCard: {
    padding: 20,
    marginBottom: 16,
    alignSelf: "center",
    minWidth: 200,
    maxWidth: 400,
    width: "100%",
    backgroundColor: "rgba(255, 255, 255, 0.85)",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "rgba(212, 165, 255, 0.25)", // Y2K lavender accent
    ...glassCardShadow,
  } as ViewStyle,

  // Section header within glass cards
  sectionHeader: {
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(212, 165, 255, 0.15)",
  },

  // Action buttons container
  actionButtons: {
    gap: 12,
    alignSelf: "center",
    width: "100%",
    maxWidth: 400,
    marginTop: 8,
  },

  // Full-width action button
  actionButton: {
    width: "100%",
  },

  // Divider between sections
  sectionDivider: {
    height: 1,
    backgroundColor: "rgba(212, 165, 255, 0.2)",
    marginVertical: 16,
    alignSelf: "center",
    width: "100%",
    maxWidth: 400,
  },

  // Issue item styling
  issueItem: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(0, 0, 0, 0.05)",
  },

  issueItemLast: {
    borderBottomWidth: 0,
  },

  // Suggestion item styling
  suggestionItem: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(0, 0, 0, 0.05)",
  },

  suggestionItemLast: {
    borderBottomWidth: 0,
  },

  // Snippet/quote styling
  snippetBox: {
    backgroundColor: "rgba(0, 0, 0, 0.03)",
    padding: 12,
    borderRadius: 8,
    marginTop: 8,
    borderLeftWidth: 3,
    borderLeftColor: "rgba(212, 165, 255, 0.5)",
  },

  // Before/After comparison box
  comparisonBox: {
    backgroundColor: "rgba(0, 0, 0, 0.02)",
    padding: 12,
    borderRadius: 8,
    marginTop: 8,
  },

  beforeBox: {
    backgroundColor: "rgba(239, 68, 68, 0.05)", // Red tint
    borderLeftColor: "#EF4444",
  },

  afterBox: {
    backgroundColor: "rgba(16, 185, 129, 0.05)", // Green tint
    borderLeftColor: "#10B981",
  },

  // Severity badge styling
  severityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    alignSelf: "flex-start",
    marginBottom: 8,
  },

  severityLow: {
    backgroundColor: "rgba(16, 185, 129, 0.15)",
  },

  severityMedium: {
    backgroundColor: "rgba(245, 158, 11, 0.15)",
  },

  severityHigh: {
    backgroundColor: "rgba(239, 68, 68, 0.15)",
  },

  severityCritical: {
    backgroundColor: "rgba(124, 58, 237, 0.15)",
  },

  // Pattern/Transformation card styling (centered, matches glassCard width)
  featureCard: {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: "rgba(212, 165, 255, 0.2)",
    // Centering (consistent with glassCard, scoreCardGradient)
    alignSelf: "center",
    width: "100%",
    maxWidth: 400,
    ...featureCardShadow,
  } as ViewStyle,

  // Tier badge for premium features
  tierBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: "rgba(124, 58, 237, 0.1)",
    alignSelf: "flex-start",
    marginBottom: 8,
  },

  // Feedback buttons row
  feedbackRow: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 16,
    marginTop: 8,
  },

  feedbackButton: {
    padding: 12,
    borderRadius: 12,
    backgroundColor: "rgba(0, 0, 0, 0.03)",
    minWidth: 60,
    alignItems: "center",
  },

  feedbackButtonActive: {
    backgroundColor: "rgba(124, 58, 237, 0.15)",
  },
});

/**
 * Severity colors for issue badges
 */
export const SEVERITY_COLORS = {
  low: {
    bg: "rgba(16, 185, 129, 0.15)",
    text: "#059669",
  },
  medium: {
    bg: "rgba(245, 158, 11, 0.15)",
    text: "#D97706",
  },
  high: {
    bg: "rgba(239, 68, 68, 0.15)",
    text: "#DC2626",
  },
  critical: {
    bg: "rgba(124, 58, 237, 0.15)",
    text: "#5B21B6",
  },
} as const;
